import type { SxProps, Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

/** Full-page shell for admin billing (follows light/dark background). */
export function adminBillingPageRootSx(theme: Theme): SxProps<Theme> {
  return {
    p: '2rem',
    bgcolor: 'background.default',
    minHeight: '100vh',
    backgroundImage: 'none',
  }
}

/** Header row under page title (back / actions). */
export function adminBillingHeaderRowSx(theme: Theme): SxProps<Theme> {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: '2rem',
    pb: '1.5rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
  }
}

/** Rounded square behind primary header / section icons. */
export function adminBillingPrimaryIconBadgeSx(theme: Theme, boxRem: number): SxProps<Theme> {
  const p = theme.palette.primary.main
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: alpha(p, 0.12),
    color: 'primary.main',
    borderRadius: '0.75rem',
    width: `${boxRem}rem`,
    height: `${boxRem}rem`,
    border: `1px solid ${alpha(p, 0.28)}`,
  }
}

/** Smaller icon badge (e.g. card section titles). */
export function adminBillingPrimaryIconBadgeSmallSx(theme: Theme): SxProps<Theme> {
  return adminBillingPrimaryIconBadgeSx(theme, 2.5)
}

const interStack = 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'

/** Outlined toolbar buttons (Back, Refresh, etc.). */
export function adminBillingToolbarOutlinedButtonSx(theme: Theme): SxProps<Theme> {
  const p = theme.palette.primary.main
  return {
    color: 'text.secondary',
    borderColor: 'divider',
    fontWeight: 500,
    fontSize: '0.875rem',
    py: 0.5,
    px: 2,
    borderRadius: '0.5rem',
    textTransform: 'none',
    fontFamily: interStack,
    '&:hover': {
      borderColor: alpha(p, 0.55),
      color: 'primary.main',
      bgcolor: alpha(p, 0.08),
      backgroundImage: 'none',
    },
    '&:disabled': {
      color: 'text.disabled',
      borderColor: 'divider',
      bgcolor: 'action.hover',
      backgroundImage: 'none',
    },
  }
}

/** Primary CTA (Init tiers, migration run, create tier) — uses palette, not fixed orange hex. */
export function adminBillingPrimaryCtaButtonSx(theme: Theme): SxProps<Theme> {
  return {
    fontWeight: 600,
    fontSize: '0.875rem',
    py: 0.5,
    px: 2,
    borderRadius: '0.5rem',
    textTransform: 'none',
    fontFamily: interStack,
    boxShadow: theme.shadows[2],
    '&:hover': {
      boxShadow: theme.shadows[6],
      transform: 'translateY(-0.125rem)',
    },
    '&:disabled': {
      bgcolor: 'action.disabledBackground',
      color: 'text.disabled',
      transform: 'none',
      boxShadow: 'none',
    },
  }
}

/** Warning-styled CTA (e.g. run migration). */
export function adminBillingWarningCtaButtonSx(theme: Theme): SxProps<Theme> {
  const w = theme.palette.warning.main
  return {
    fontWeight: 600,
    fontSize: '0.875rem',
    py: 0.5,
    px: 2,
    borderRadius: '0.5rem',
    textTransform: 'none',
    fontFamily: interStack,
    boxShadow: theme.shadows[2],
    bgcolor: w,
    color: theme.palette.warning.contrastText,
    '&:hover': {
      bgcolor: theme.palette.warning.dark,
      boxShadow: theme.shadows[6],
      transform: 'translateY(-0.125rem)',
    },
    '&:disabled': {
      bgcolor: 'action.disabledBackground',
      color: 'text.disabled',
      transform: 'none',
      boxShadow: 'none',
    },
  }
}

export function adminBillingErrorAlertSx(theme: Theme): SxProps<Theme> {
  const e = theme.palette.error.main
  return {
    mb: '1.5rem',
    bgcolor: alpha(e, 0.08),
    backgroundImage: 'none',
    border: '1px solid',
    borderColor: alpha(e, 0.35),
    borderRadius: '0.75rem',
    color: 'error.main',
    '& .MuiAlert-icon': { color: 'error.main' },
    '& .MuiAlert-message': {
      color: 'error.main',
      fontFamily: interStack,
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  }
}

export function adminBillingErrorAlertCompactSx(theme: Theme): SxProps<Theme> {
  const e = theme.palette.error.main
  return {
    mb: '1rem',
    bgcolor: alpha(e, 0.08),
    backgroundImage: 'none',
    border: '1px solid',
    borderColor: alpha(e, 0.35),
    borderRadius: '0.75rem',
    color: 'error.main',
    '& .MuiAlert-icon': { color: 'error.main' },
    '& .MuiAlert-message': {
      color: 'error.main',
      fontFamily: interStack,
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  }
}

/** Elevated card with hover lift and primary-tinted border. */
export function adminBillingHoverCardSx(
  theme: Theme,
  radius: string | number = '1rem',
): SxProps<Theme> {
  const p = theme.palette.primary.main
  return {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: radius,
    boxShadow: theme.shadows[2],
    transition: theme.transitions.create(['transform', 'box-shadow', 'border-color'], {
      duration: theme.transitions.duration.standard,
    }),
    '&:hover': {
      transform: 'translateY(-0.125rem)',
      boxShadow: theme.shadows[6],
      borderColor: alpha(p, 0.45),
    },
  }
}

/** Muted stat / info inset (replaces #f8fafc + gray border). */
export function adminBillingInsetStatBoxSx(theme: Theme): SxProps<Theme> {
  return {
    p: '1rem',
    bgcolor: 'action.hover',
    backgroundImage: 'none',
    borderRadius: '0.75rem',
    border: '1px solid',
    borderColor: 'divider',
  }
}

export function adminBillingStatLabelTypographySx(): SxProps<Theme> {
  return {
    color: 'text.secondary',
    mb: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    fontFamily: interStack,
  }
}

/** Migration “old system” / caution stat tile. */
export function adminBillingWarningStatBoxSx(theme: Theme): SxProps<Theme> {
  const w = theme.palette.warning.main
  return {
    p: '1rem',
    bgcolor: alpha(w, 0.12),
    backgroundImage: 'none',
    borderRadius: '0.75rem',
    border: '1px solid',
    borderColor: alpha(w, 0.45),
  }
}

export function adminBillingWarningStatTextSx(theme: Theme): SxProps<Theme> {
  return {
    color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark,
    fontSize: '0.875rem',
    fontWeight: 500,
    fontFamily: interStack,
    mb: '0.5rem',
  }
}

/** Migrated / success stat tile. */
export function adminBillingSuccessStatBoxSx(theme: Theme): SxProps<Theme> {
  const s = theme.palette.success.main
  return {
    p: '1rem',
    bgcolor: alpha(s, 0.1),
    backgroundImage: 'none',
    borderRadius: '0.75rem',
    border: '1px solid',
    borderColor: alpha(s, 0.4),
  }
}

export function adminBillingSuccessStatTextSx(theme: Theme): SxProps<Theme> {
  return {
    color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
    fontSize: '0.875rem',
    fontWeight: 500,
    fontFamily: interStack,
    mb: '0.5rem',
  }
}

/** Standard outlined TextField chrome in this screen. */
export function adminBillingTextFieldOutlinedSx(theme: Theme): SxProps<Theme> {
  const p = theme.palette.primary.main
  return {
    '& .MuiInputBase-input': {
      color: 'text.primary',
      fontSize: '0.875rem',
      fontFamily: interStack,
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      fontSize: '0.875rem',
      fontFamily: interStack,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'divider',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(p, 0.55),
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
    },
  }
}

/** Neutral pricing chip (base price, per user, …). */
export function adminBillingPricingMetaChipSx(theme: Theme): SxProps<Theme> {
  return {
    color: 'text.primary',
    fontSize: '0.875rem',
    fontFamily: interStack,
    bgcolor: 'action.selected',
    backgroundImage: 'none',
    py: 0.25,
    px: 0.5,
    borderRadius: '0.375rem',
    border: '1px solid',
    borderColor: 'divider',
  }
}

/** “Contact sales” pricing callout. */
export function adminBillingSalesContactChipSx(theme: Theme): SxProps<Theme> {
  const w = theme.palette.warning.main
  return {
    color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark,
    fontWeight: 600,
    fontSize: '0.875rem',
    fontFamily: interStack,
    bgcolor: alpha(w, 0.15),
    backgroundImage: 'none',
    py: 0.25,
    px: 0.5,
    borderRadius: '0.375rem',
    border: `1px solid ${alpha(w, 0.45)}`,
  }
}

export function adminBillingDividerTopSx(theme: Theme): SxProps<Theme> {
  return {
    borderTop: `1px solid ${theme.palette.divider}`,
  }
}
